<template>
  <div class="point-wrap"
    :class="computedCssClass"
    :style="{
      left: `${point.x}px`,
      top: `${point.y}px`,
      transform: `scale(${computedScale})`,
      'z-index': isHeatmapDownloading ? (100000 - parseInt(point.y)) : null
    }"
    @mouseenter="$emit('setHoverIndex', index)"
    @mouseleave="$emit('setHoverIndex', null)">
    <div
      ref="point"
      class="point"
      :class="computedCssClass"
      @mousedown="$emit('onPointMouseDown', index, $event)"
      @mouseup="$emit('onPointMouseUp')"
      @click.stop>
      <span class="text">
        {{ index + 1 }}
      </span>
      <template v-if="overlapItems && overlapItems.length">
        <div v-for="overlapNo in Math.min(overlapItems.length - 1, 5)" :key="overlapNo"
          class="point-overlap"
          :style="{
            marginTop: `${overlapNo * 3}px`,
            marginLeft: `${overlapNo * 3}px`,
            zIndex: -overlapNo
          }">
        </div>
      </template>
    </div>
    <div v-if="isShowOverlapItems" class="tooltip-wrap"
      :style="tooltipData ? tooltipData.style : null"
      :class="{ 'bottom-align': tooltipData && tooltipData.arrow.isTop }">
      <ui-tooltip
        v-show="isShowOverlapItems"
        ref="tooltip"
        small
        noApplyCenterAnimate
        :arrowCenter="tooltipData && tooltipData.arrow.isCenter"
        :arrowRight="tooltipData && tooltipData.arrow.isRight"
        :arrowTop="tooltipData && tooltipData.arrow.isTop"
        class="overlap-tooltip"
        :class="{ 'with-control': overlapItems.length > 3 }"
        :style="{ visibility: tooltipData ? 'visible' : 'hidden' }">
        <button type="button" class="btn-control btn-prev"
          v-if="overlapShowIndex > 0"
          @click.stop="onOverlapControlClick('prev')">
          <i class="icon icon--prev-gray-size16 hover-style-black"></i>
        </button>
        <button type="button" v-for="(overlapIndex, index) of overlapItems"
          :key="overlapIndex"
          class="overlap-index"
          v-show="index >= overlapShowIndex && index <= overlapShowIndex + 2"
          @click.stop="$emit('onOverlapIndexClick', overlapIndex)">
          <span>{{ overlapIndex + 1 }}</span>
        </button>
        <button type="button" class="btn-control btn-next"
          v-if="overlapShowIndex < overlapItems.length - 3"
          @click.stop="onOverlapControlClick('next')">
          <i class="icon icon--prev-gray-size16 hover-style-black rotate"></i>
        </button>
      </ui-tooltip>
    </div>
    <div v-if="isShowOverlapItemsOnDownloading" class="tooltip-on-downloading-wrap"
      :style="tooltipData ? tooltipData.style : null"
      :class="{ 'bottom-align': tooltipData && tooltipData.arrow.isTop }">
      <ui-tooltip
        small
        noApplyCenterAnimate
        arrowCenter
        arrowTop>
        {{overlapItems.map(x => x + 1).join(', ')}}
      </ui-tooltip>
    </div>
  </div>
</template>

<script>
import UiTooltip from '@/components/ui/UiTooltip'

export default {
  name: 'HeatmapStreamNode',
  components: {
    UiTooltip
  },
  props: [
    'cssClass',
    'containerScale',
    'containerWidth',
    'containerHeight',
    'item',
    'index',
    'pointItems',
    'actionData',
    'overlapIndexList',
    'isHeatmapDownloading'
  ],
  data () {
    return {
      overlapShowIndex: 0,
      tooltipData: null
    }
  },
  computed: {
    point () {
      let x = this.item.x
      let y = this.item.y

      if (this.item.temp) {
        x = this.item.temp.x
        y = this.item.temp.y
      }
      return {
        x,
        y
      }
    },
    computedScale () {
      return Math.min(Math.max(this.containerScale, 0.8), 1)
    },
    computedCssClass () {
      const isInactive = this.actionData.activeIndex !== null && !this.cssClass.active &&
        !this.cssClass['active-prev'] && !this.cssClass['active-next']

      return {
        ...this.cssClass,
        moved: !!this.item.temp,
        inactive: isInactive
      }
    },
    isShowOverlapItems () {
      return this.overlapItems && this.cssClass.hover && !this.cssClass.active && !this.item.temp
    },
    isShowOverlapItemsOnDownloading () {
      return this.overlapItems && this.isHeatmapDownloading === true && !this.item.temp && this.overlapItems[0] === this.index
    },
    overlapItems () {
      if (this.overlapIndexList) {
        return [...this.overlapIndexList].sort((a, b) => {
          return a - b
        })
      }
      return null
    }
  },
  watch: {
    isShowOverlapItems (value) {
      if (value) {
        this.$nextTick().then(() => {
          const pointRect = {
            width: this.$refs.point.offsetWidth,
            height: this.$refs.point.offsetHeight
          }
          const tooltipRect = {
            width: this.$refs.tooltip.$el.offsetWidth,
            height: this.$refs.tooltip.$el.offsetHeight
          }
          let isTop = false
          let isCenter = true
          let isLeft = false
          let isRight = false
          let top
          let left

          const x = this.item.x
          const y = this.item.y
          const minPaddingV = pointRect.height / 2 + tooltipRect.height + 10
          const minPaddingH = tooltipRect.width / 2 + 10

          if (y < minPaddingV) {
            isTop = true
          }

          if (x < minPaddingH) {
            isCenter = false
            isLeft = true
            isRight = false
          }

          if (this.containerWidth - x < minPaddingH) {
            isCenter = false
            isLeft = false
            isRight = true
          }

          if (isTop) {
            top = pointRect.height / 2 - 13
          } else {
            top = -pointRect.height / 2 - tooltipRect.height
          }

          if (isLeft) {
            left = -18
          } else if (isRight) {
            left = -tooltipRect.width + 20
          } else {
            left = -tooltipRect.width / 2
          }

          this.tooltipData = {
            style: {
              width: `${tooltipRect.width}px`,
              height: `${tooltipRect.height + 15}px`,
              top: `${top}px`,
              left: `${left}px`
            },
            arrow: {
              isCenter,
              isRight,
              isTop
            }
          }
        })
      } else {
        this.tooltipData = null
        this.overlapShowIndex = 0
      }
    }
  },
  methods: {
    onOverlapControlClick (type) {
      if (type === 'prev' && this.overlapShowIndex > 0) {
        this.overlapShowIndex--
      } else if (type === 'next' && this.overlapShowIndex < this.overlapItems.length - 2) {
        this.overlapShowIndex++
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.point-wrap {
  position: absolute;

  &.hover {
    z-index: 10;

    &.overlap {
      z-index: 30;
    }
  }

  &.active-prev,
  &.active-next {
    z-index: 5;
  }

  &.active {
    z-index: 20;
  }
}

.point {
  position: absolute;
  box-sizing: border-box;
  width: 48px;
  height: 48px;
  top: 0;
  left: 0;
  margin-top: -24px;
  margin-left: -24px;
  border-radius: 50%;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  background-color: #fff;
  cursor: pointer;

  .text {
    @include positionXYcenter;
    font-weight: bold;
    color: $brandColor;
    font-size: 14px;
  }

  &.overlap {
    &:not(.moved) {
      background-color: #fffaba;
    }
    &:not(.active):not(.hide):not(.moved) .point-overlap {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: inherit;
      box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.5);
      z-index: -1;
    }
  }

  &.inactive {
    width: 36px;
    height: 36px;
    margin-left: -18px;
    margin-top: -18px;
    background-color: #fff;

    .text {
      color: #999;
    }
  }

  &.hide {
    display: none;
  }

  &.active,
  &.hover {
    display: block;
    width: 74px;
    height: 74px;
    margin-top: -37px;
    margin-left: -37px;

    .text {
      font-size: 18px;
    }
  }

  &.hover {
    .text {
      color: #1199af;
    }
  }

  &.active {
    display: block;
    cursor: default;
    border: solid 2px #ffffff;
    background-color: #ec0047 !important;

    .text {
      color: #fff !important;
    }
  }

  &.active-prev,
  &.active-next {
    background-color: #fff;

    &.text {
      color: $brandColor;
    }
  }

  &.hover {
    cursor: pointer;
  }
  &.move {
    display: block;
    cursor: move;
  }
  &.moved {
    display: block;
  }
}

.tooltip-wrap {
  position: absolute;

  &.bottom-align .overlap-tooltip {
    margin-top: 15px;
  }
}

.tooltip-on-downloading-wrap {
  position: absolute;
  top: 40px;
  left: -46px;
  opacity: 0.55;

  & /deep/ .bubble {
    width: 94px;

    .text {
      font-size: 14px;
      line-height: 1.23;
      color: #2f2f2f;
      white-space: normal;
      width: 62px;
      text-align: center;
    }
  }
}

.overlap-tooltip {
  position: absolute;
  display: block;
  height: 63px;
  padding: 15px 15px 0 15px;
  cursor: default;
  z-index: 30;

  &.with-control {
    padding-left: 24px;
    padding-right: 24px;

    .btn-control {
      @include positionYcenter;
      display: block;
      margin-top: 2px;

      &.btn-prev {
        left: 5px;
      }

      &.btn-next {
        right: 5px;
      }

      .icon--prev-gray-size16.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .btn-control {
    display: none;
  }

  .overlap-index {
    position: relative;
    display: inline-block;
    width: 33px;
    height: 33px;
    margin: 0 2px;
    box-sizing: border-box;
    border-radius: 50%;
    border: 1px solid #c1c1c1;

    &> span {
      @include positionXYcenter;
      font-size: 12px;
      color: $brandColor;
      font-weight: bold;
      margin-top: 1px;
    }

    &:hover {
      background-color: rgba(77, 62, 62, 0.06);
      &> span {
        color: #1199af;
      }
    }
  }
}
</style>
